import { useEffect } from 'react'
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from '../../components/ConfirmationModal'
import DebugAccordion from '../../components/DebugAccordion'
import JsonForm from '../../components/JsonForm.jsx'
import Loadable from '../../components/Loadable'
import PageTitle from '../../components/PageTitle.jsx'
import ConfigTemplateNav, { ConfigTemplateSubNav } from './components/ConfigTemplateNav'
import ConfigTemplateApiInterfaceChecklist from './components/ConfigTemplateApiInterfaceChecklist'

import { NotificationTypes, addNotification } from '../../state/actions/notificationActions'
import { fetchConfigTemplate, updateConfigTemplate } from '../../state/actions/configTemplateActions'
import { fetchComponentConfigSchema } from '../../state/actions/componentConfigSchemaActions'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'

import { configTemplateSignal, configTemplateLoadedSignal, configTemplateErrorSignal } from '../../state/signals/configTemplateSignals'
import { componentConfigSchemaSignal, componentConfigSchemaLoadedSignal, componentConfigSchemaErrorSignal } from '../../state/signals/componentConfigSchemaSignals'

import { buildApiInterfaceCheckedSetFromConfig, buildFilteredApiInterfaceSchema } from './configTemplateSchemaUtils'
import { checkApiInterfaceConflicts, isEmptyObject, makeFieldsReadonly } from '../../utils/utils.js'
import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

export default function ConfigTemplateApiInterfaceDetail() {

    const navigate = useNavigate()
    const { beId, configTemplateId, id } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/templates`)
    const [searchParams] = useSearchParams()
    // taskDesc identifies an interface within a template, matching the server-side
    // merge key (cdType + taskDesc) rather than listenPort.
    const desc = searchParams.get('desc')
    const editMode = desc !== null
    const debug = !!searchParams.get('debug')
    const { config, accessToken, userPermissionsSummary } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewConfigTemplates) {
            addNotification('You are not authorized to view config templates for this account.', NotificationTypes.ERROR, true)
            navigate('/')
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => userPermissionsSummary?.value?.canEditConfigTemplates)

    useEffect(() => {
        if (beIdChanged) return
        fetchSchemaManifest()
        fetchConfigTemplate(beId, configTemplateId, config, accessToken).then(() => {
            const version = configTemplateSignal.value?.utgSchemaVersion || '5.0.0.3303'
            fetchComponentConfigSchema(beId, version, config, accessToken)
        })
    }, [beId, beIdChanged, configTemplateId, config, accessToken])

    const parsedConfigComputed = useComputed(() => {
        try { return JSON.parse(configTemplateSignal.value?.config || '{}') } catch { return {} }
    })

    const apiInterfaceIndexComputed = useComputed(() => {
        if (!configTemplateLoadedSignal.value || !editMode) return -1
        const interfaces = parsedConfigComputed.value?.apiInterfaceConfig?.apiInterface
        if (!interfaces) return -1
        return interfaces.findIndex(a => a.cdType == parseInt(id, 10) && (a.taskDesc ?? '') == desc)
    })

    const apiInterfaceComputed = useComputed(() => {
        if (!configTemplateLoadedSignal.value || !editMode) return null
        const index = apiInterfaceIndexComputed.value
        if (index < 0) return null
        const current = parsedConfigComputed.value.apiInterfaceConfig.apiInterface[index]
        return { config: current }
    })

    const apiInterfaceSchemaComputed = useComputed(() => {
        if (!componentConfigSchemaSignal.value?.properties) return {}
        const item = componentConfigSchemaSignal.value?.properties?.config?.properties?.apiInterfaceConfig?.properties?.apiInterface?.items?.find(
            a => a.properties.cdType['s4-model-key'] == parseInt(id, 10)
        )
        return { config: item }
    })

    const pageTitleComputed = useComputed(() => {
        const caption = apiInterfaceSchemaComputed.value?.config?.properties?.cdType?.['s4-caption'] || ''
        return editMode ? `Manage ${caption} API Interface` : `Add ${caption} API Interface`
    })

    // Field selection for this interface type only. Seeded from the interface already
    // stored on the template so an existing entry opens with its own fields checked.
    const checkedFieldsSignal = useSignal(new Set())

    useSignalEffect(() => {
        if (configTemplateLoadedSignal.value && componentConfigSchemaLoadedSignal.value) {
            const stored = apiInterfaceComputed.value?.config
            checkedFieldsSignal.value = stored
                ? buildApiInterfaceCheckedSetFromConfig([stored])
                : new Set()
        }
    })

    // The checklist renders a section per interface type; scope it to the one being edited.
    const currentInterfaceSchemaComputed = useComputed(() => {
        const item = apiInterfaceSchemaComputed.value?.config
        if (!item) return null
        return {
            properties: {
                config: {
                    properties: {
                        apiInterfaceConfig: {
                            properties: { apiInterface: { items: [item] } }
                        }
                    }
                }
            }
        }
    })

    const showCancelModalSignal = useSignal(false)
    const isFormEditedSignal = useSignal(false)
    const isFormSubmittingSignal = useSignal(false)

    const handleFormSubmit = values => {
        if (isFormSubmittingSignal.value) return false
        isFormSubmittingSignal.value = true
        try {
            if (values) {
                const parsed = parsedConfigComputed.value
                const updatedConfig = structuredClone(parsed)
                updatedConfig.apiInterfaceConfig = updatedConfig.apiInterfaceConfig || {}
                updatedConfig.apiInterfaceConfig.apiInterface = updatedConfig.apiInterfaceConfig.apiInterface || []

                // index is -1 when adding, or when ?desc= names an entry the template
                // no longer has; either way this is an insert rather than a replace
                const index = apiInterfaceIndexComputed.value
                if (editMode && index >= 0) {
                    updatedConfig.apiInterfaceConfig.apiInterface[index] = values.config
                } else {
                    updatedConfig.apiInterfaceConfig.apiInterface.push(values.config)
                }

                if (checkApiInterfaceConflicts(updatedConfig.apiInterfaceConfig.apiInterface)) {
                    addNotification('Unable to save due to conflict. All API Interfaces must have a unique Port.', NotificationTypes.ERROR)
                    isFormSubmittingSignal.value = false
                    return false
                }

                updateConfigTemplate(beId, { ...configTemplateSignal.value, config: JSON.stringify(updatedConfig) }, config, accessToken)
                    .then(result => {
                        configTemplateSignal.value = result
                        navigate(`/be/${beId}/templates/${configTemplateId}/apiinterfaces`)
                        addNotification('API Interface was successfully updated.', NotificationTypes.SUCCESS, true)
                    })
                    .catch(error => {
                        addNotification(`Unable to update the API Interface. ${error.toString()}`, NotificationTypes.ERROR, false)
                    })
                    .finally(() => {
                        isFormSubmittingSignal.value = false
                    })
            }
        } catch (error) {
            addNotification(`Unable to update the API Interface. ${error.toString()}`, NotificationTypes.ERROR, false)
            isFormSubmittingSignal.value = false
        }
        return false
    }

    const handleCancel = () => {
        if (isFormEditedSignal.value) {
            showCancelModalSignal.value = true
        } else {
            navigate('../apiinterfaces')
        }
    }

    const handleFormChange = () => {
        isFormEditedSignal.value = true
    }

    const jsonFormDataComputed = useComputed(() => {
        if (!componentConfigSchemaLoadedSignal.value || !configTemplateLoadedSignal.value) return null
        if (isEmptyObject(componentConfigSchemaSignal.value)) return null
        if (checkedFieldsSignal.value.size === 0) return null

        // Restrict the form to the fields checked on the left. buildFilteredApiInterfaceSchema
        // returns the matching item types; this page only ever renders one.
        const filteredItems = buildFilteredApiInterfaceSchema(componentConfigSchemaSignal.value, checkedFieldsSignal.value)
        const filteredItem = filteredItems.find(
            item => item.properties?.cdType?.['s4-model-key'] == parseInt(id, 10)
        )
        if (!filteredItem) return null

        let clonedSchema = structuredClone({ config: filteredItem })
        if (!canEditComputed.value) clonedSchema = makeFieldsReadonly(clonedSchema)

        globalThis.SCHEMA_MODE = 'CONFIG'

        const formData = {
            schema: clonedSchema,
            loadedComponentConfig: parsedConfigComputed.value,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: canEditComputed.value
        }
        if (apiInterfaceComputed.value) {
            formData.value = apiInterfaceComputed.value
        }
        return formData
    })

    const accordionData = [
        { name: 'API Interface Data', data: apiInterfaceComputed.value },
        { name: 'API Interface Schema', data: apiInterfaceSchemaComputed.value },
        { name: 'Config Template', data: configTemplateSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    return (
        <>
            <ConfirmationModal
                show={showCancelModalSignal.value}
                handleCancel={() => showCancelModalSignal.value = false}
                handleConfirm={() => navigate('../apiinterfaces')}
                message="You have unsaved changes to this form. Are you sure you want to leave and lose these changes?"
                title="Warning: Unsaved Changes"
                cancelText="Continue Editing"
                confirmText="Lose Changes"
            />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/templates` }}>
                    Config Template List
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/templates/${configTemplateId}/apiinterfaces` }}>
                    {configTemplateLoadedSignal.value ? <>Manage {configTemplateSignal.value?.name}</> : <>Loading...</>}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    {pageTitleComputed.value}
                </Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title={pageTitleComputed.value} canEditSignal={canEditComputed} />
            </Stack>
            <ConfigTemplateNav activeKey="2" />
            <ConfigTemplateSubNav activeKey="3" />
            <Loadable loadedSignals={[configTemplateLoadedSignal, componentConfigSchemaLoadedSignal]} errorSignals={[configTemplateErrorSignal, componentConfigSchemaErrorSignal]}>
                <div className="d-flex gap-3 px-3">
                    <div style={{ width: '300px', flexShrink: 0, overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                        <ConfigTemplateApiInterfaceChecklist
                            schema={currentInterfaceSchemaComputed.value}
                            checkedFieldsSignal={checkedFieldsSignal}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {checkedFieldsSignal.value.size === 0 ? (
                            <p className="text-muted mt-3">Select API Interface fields on the left to include them in this config template.</p>
                        ) : (
                            <JsonForm data={jsonFormDataComputed.value} />
                        )}
                    </div>
                </div>
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}
